/* eslint-disable jsx-a11y/label-has-associated-control, no-unused-vars */
import React, {
  useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import NumberFormat from 'react-number-format';
import { useSelector } from 'react-redux';
import { selectRoomStatusById } from '@redux/selectors/FormSelector';

const RoomMeasurement = ({
  id, formik, handleBlur,
}) => {
  const didMount = useRef(false); // stateflag...
  const [inchesL, setInchesL] = useState('');
  const [feetL, setFeetL] = useState('');
  const [inchesW, setInchesW] = useState('');
  const [feetW, setFeetW] = useState('');
  const [imperialW, setImperialW] = useState('');
  const [imperialL, setImperialL] = useState('');
  const [metricW, setMetricW] = useState('');
  const [metricL, setMetricL] = useState('');

  const roomStatus = useSelector(state => selectRoomStatusById(state, id)); // room record meta
  const MAX_INCHES_VAL = 11; // arithmetic helper
  const MAX_SIZE_FEET = 200;
  const MAX_SIZE_METERS = 61.24;

  const withFeetValueLimit = ({ formattedValue, floatValue }) => formattedValue === '' || floatValue <= MAX_SIZE_FEET;
  const withMeterValueLimit = ({ formattedValue, floatValue }) => formattedValue === '' || floatValue <= MAX_SIZE_METERS;
  const withInchValueLimit = ({ formattedValue, floatValue }) => formattedValue === '' || floatValue <= MAX_INCHES_VAL; // unknown control.

  const roomValues = formik?.values?.Rooms[id]; // values for given room
  const setFormikValue = (fieldName, val) => formik.setFieldValue(fieldName, val);

  const metricDecimalToImperialInts = metricDecimal => {
    const imperialDecimal = metricDecimal * 3.28084;
    let feetInt = Math.floor(imperialDecimal);
    let inchesInt = (imperialDecimal - feetInt) * 12;
    if (inchesInt > 11) {
      feetInt += 1;
      inchesInt = 0;
    }
    return {
      feet: feetInt,
      inches: inchesInt,
    };
  };

  const imperialDecimalToImperialInts = impDecimal => {
    let feetInt = Math.floor(impDecimal);
    let inchesInt = (impDecimal - feetInt) * 12;
    if (inchesInt > 11) {
      feetInt += 1;
      inchesInt = 0;
    }
    return {
      feet: feetInt,
      inches: inchesInt,
    };
  };

  const feetInchesToFeetDecimal = (feet = 0, inches = 0) => {
    const numericFeet = Number(feet);
    const inchDecimal = inches / 12;
    return numericFeet + inchDecimal;
  };

  const imperialIntsToImperialDecimal = (feet, inches) => feet + Math.round((inches / 12) * 100 + Number.EPSILON) / 100;
  const imperialDecimalToMetricDecimal = imperialDecimal => Math.round((imperialDecimal / 3.28084) * 1000 + Number.EPSILON) / 1000;

  // sets UI reading from formik values, converts long to int array if imperial
  // only runs once
  useEffect(() => {
    if (roomValues?.RoomLengthWidthUnits === 'M') { // handles metric
      if (roomValues.RoomLength) setMetricL(roomValues.RoomLength);
      if (roomValues.RoomWidth) setMetricW(roomValues.roomWidth);
    }

    if (roomValues?.RoomLengthWidthUnits === 'F') { // handles imperial
      if (roomValues.RoomLength) {
        const imperialInts = imperialDecimalToImperialInts(roomValues.RoomLength);
        setFeetL(imperialInts.feet);
        setInchesL(imperialInts.inches);
        setImperialL(feetInchesToFeetDecimal(feetL, inchesL));
      }

      if (roomValues?.RoomWidth) {
        const imperialInts = imperialDecimalToImperialInts(roomValues.RoomWidth);
        setFeetW(imperialInts.feet);
        setInchesW(imperialInts.inches);
        setImperialW(feetInchesToFeetDecimal(feetW, inchesW));
      }
    }
  }, []);

  useEffect(() => {
    if (didMount.current) { // in place to not run the conversion on mount, because dimension unit is set on mount
      if (roomValues.RoomLengthWidthUnits === 'F') {
        if (roomValues.RoomLength) { // convert metric decimal to imperial
          const imperialInts = metricDecimalToImperialInts(roomValues.RoomLength);
          const imperialDecimal = imperialIntsToImperialDecimal(imperialInts.feet, imperialInts.inches);
          setFormikValue(`Rooms.[${id}].RoomLength`, imperialDecimal);
          setInchesL(imperialInts.inches);
          setFeetL(imperialInts.feet);
          setImperialL(feetInchesToFeetDecimal(feetL, inchesL));
        }
        if (roomValues.RoomWidth) {
          const imperialInts = metricDecimalToImperialInts(roomValues.RoomWidth);
          const imperialDecimal = imperialIntsToImperialDecimal(imperialInts.feet, imperialInts.inches);
          setFormikValue(`Rooms.[${id}].RoomWidth`, imperialDecimal);
          setInchesW(imperialInts.inches);
          setFeetW(imperialInts.feet);
          setImperialW(feetInchesToFeetDecimal(feetW, inchesW));
        }
      }
      if (roomValues.RoomLengthWidthUnits === 'M') {
        if (roomValues.RoomLength) {
          const metricDecimal = imperialDecimalToMetricDecimal(roomValues.RoomLength);
          setFormikValue(`Rooms.[${id}].RoomLength`, metricDecimal);
          setMetricL(metricDecimal);
        }
        if (roomValues.RoomWidth) {
          const metricDecimal = imperialDecimalToMetricDecimal(roomValues.RoomWidth);
          setFormikValue(`Rooms.[${id}].RoomWidth`, metricDecimal);
          setMetricW(metricDecimal);
        }
      }
    } else didMount.current = true;
  }, [roomValues?.RoomLengthWidthUnits]);

  // blur handlers for length/width, uninvolved in conversions
  // formik state values are updated on blur
  // handleblur passed from props.
  useEffect(() => {
    handleBlur(`Rooms.[${id}].RoomLength`)();
    if (roomValues.RoomLengthWidthUnits === 'M') {
      if (roomValues.RoomLength === metricL) return;
      setMetricL(roomValues?.RoomLength);
    }
    if (roomValues.RoomLengthWidthUnits === 'F') {
      if (roomValues.RoomLength === imperialL) return;
      setImperialL(roomValues?.RoomLength);
    }
  }, [roomValues?.RoomLength]);

  // handleblur passeed from props
  // formik state updated
  useEffect(() => {
    handleBlur(`Rooms.[${id}].RoomWidth`)();
    if (roomValues.RoomLengthWidthUnits === 'M') {
      if (roomValues.RoomWidth === metricW) return;
      setMetricW(roomValues?.RoomWidth);
    }
    if (roomValues.RoomLengthWidthUnits === 'F') {
      if (roomValues.RoomWidth === imperialW) return;
      setImperialW(roomValues?.RoomWidth);
    }
  }, [roomValues?.RoomWidth]);

  const dimensionChangeHandler = e => {
    if (roomValues.RoomLengthWidthUnits === 'F') {
      const val = e.target.value.replace(/"/g, '');
      if (e.target.name === 'RoomWidthInch') {
        setInchesW(val);
        setImperialW(feetInchesToFeetDecimal(feetW, val));
      }
      if (e.target.name === 'RoomWidth') {
        setFeetW(val);
        setImperialW(feetInchesToFeetDecimal(val, inchesW));
      }

      if (e.target.name === 'RoomLengthInch') {
        setInchesL(val);
        setImperialL(feetInchesToFeetDecimal(feetL, val));
      }
      if (e.target.name === 'RoomLength') {
        setFeetL(val);
        setImperialL(feetInchesToFeetDecimal(val, inchesL));
      }
    }

    if (roomValues.RoomLengthWidthUnits === 'M') {
      if (e.target.name === 'RoomWidth') {
        setMetricW(e.target.value);
      }
      if (e.target.name === 'RoomLength') {
        setMetricL(e.target.value);
      }
    }
  };

  const blurWidthHandler = () => {
    if (roomValues.RoomLengthWidthUnits === 'M') {
      if (roomValues.RoomWidth === metricW) return;
      setFormikValue(`Rooms.[${id}].RoomWidth`, metricW);
    }
    if (roomValues.RoomLengthWidthUnits === 'F') {
      if (roomValues.RoomWidth === imperialW) return;
      setFormikValue(`Rooms.[${id}].RoomWidth`, imperialW);
    }
  };

  const blurLengthHandler = () => {
    if (roomValues.RoomLengthWidthUnits === 'M') {
      if (roomValues.RoomLength === metricL) return;
      setFormikValue(`Rooms.[${id}].RoomLength`, metricL);
    }
    if (roomValues.RoomLengthWidthUnits === 'F') {
      if (roomValues.RoomLength === imperialL) return;
      setFormikValue(`Rooms.[${id}].RoomLength`, imperialL);
    }
  };

  return (
    <>
      <div className="form-row mb-1">
        <div className="form-group col-xl-6 col-lg-12">
          <label htmlFor={`Rooms.[${id}].RoomLength`} className="form-label">
            Length
          </label>
          <div
            id={`Rooms.[${id}].RoomLength`}
            className={roomValues?.RoomLengthWidthUnits === 'F' ? '' : ''}
          >
            <div className="input-group">
              <NumberFormat
                className="form-control"
                name="RoomLength"
                decimalScale={roomValues?.RoomLengthWidthUnits === 'F' ? 0 : 2}
                isAllowed={roomValues?.RoomLengthWidthUnits === 'F' ? withFeetValueLimit : withMeterValueLimit}
                type="text"
                suffix={roomValues?.RoomLengthWidthUnits === 'F' ? '' : ''}
                onBlur={blurLengthHandler}
                onChange={dimensionChangeHandler}
                value={roomValues?.RoomLengthWidthUnits === 'F' ? feetL : metricL}
              />
              <div className="input-group-append">
                <span className="input-group-text">{roomValues?.RoomLengthWidthUnits === 'F' ? 'ft' : 'm'}</span>
              </div>
              {roomValues?.RoomLengthWidthUnits === 'F' && (
                <>
                  <NumberFormat
                    className="form-control"
                    type="text"
                    name="RoomLengthInch"
                    decimalScale={0}
                    isAllowed={withInchValueLimit}
                    onBlur={blurLengthHandler}
                    onChange={dimensionChangeHandler}
                    value={inchesL}
                  />
                  <div className="input-group-append">
                    <span className="input-group-text">{roomValues?.RoomLengthWidthUnits === 'F' ? 'in' : 'm'}</span>
                  </div>
                </>
              )}
            </div>
          </div>
          {roomStatus?.RoomLength?.errorMessage && <div className="invalid-feedback">{roomStatus.RoomLength.errorMessage}</div>}
        </div>
        <div className="form-group col-xl-6 col-lg-12">
          <label htmlFor={`Rooms.[${id}].RoomWidth`} className="form-label">
            Width
          </label>
          <div
            id={`Rooms.[${id}].RoomWidth`}
            className={roomValues?.RoomLengthWidthUnits === 'F' ? '' : ''}
          >
            <div className="input-group">
              <NumberFormat
                className="form-control"
                name="RoomWidth"
                decimalScale={roomValues?.RoomLengthWidthUnits === 'F' ? 0 : 2}
                isAllowed={roomValues?.RoomLengthWidthUnits === 'F' ? withFeetValueLimit : withMeterValueLimit}
                type="text"
                onBlur={blurWidthHandler}
                onChange={dimensionChangeHandler}
                value={roomValues?.RoomLengthWidthUnits === 'F' ? feetW : metricW}
              />
              <div className="input-group-append">
                <span className="input-group-text">{roomValues?.RoomLengthWidthUnits === 'F' ? 'ft' : 'm'}</span>
              </div>
              {roomValues?.RoomLengthWidthUnits === 'F' && (
              <>
                <NumberFormat
                  className="form-control"
                  type="text"
                  name="RoomWidthInch"
                  decimalScale={0}
                  isAllowed={withInchValueLimit}
                  onBlur={blurWidthHandler}
                  onChange={dimensionChangeHandler}
                  value={inchesW}
                />
                <div className="input-group-append">
                  <span className="input-group-text">{roomValues?.RoomLengthWidthUnits === 'F' ? 'in' : 'm'}</span>
                </div>
              </>
              )}
            </div>
          </div>
          {roomStatus?.RoomWidth?.errorMessage && <div className="invalid-feedback">{roomStatus.RoomWidth.errorMessage}</div>}
        </div>
      </div>
    </>
  );
};

RoomMeasurement.propTypes = {
  formik: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.bool, PropTypes.shape(), PropTypes.number, PropTypes.func])),
  id: PropTypes.string.isRequired,
  handleBlur: PropTypes.func.isRequired,
};

export default RoomMeasurement;
